import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Bar, BarChart, CartesianGrid, Cell, Legend,
  Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts'
import { useQuery } from '@tanstack/react-query'
import { itemsService } from '../services/api'
import { useCartsQuery } from '../hooks/useCartsQuery'
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion'
import { useIsNarrow, useMediaQuery } from '../hooks/useMediaQuery'
import GlassOverlay from './GlassOverlay'
import CloseGlyph from './CloseGlyph'
import AnalyticsPeriodSlider from './AnalyticsPeriodSlider'
import CumulativeValueChart from './CumulativeValueChart'
import {
  analyticsSeriesColors,
  buildAnalyticsModel,
  chartDetailsFor,
  consumeAnalyticsScroll,
  formatEuroAxisCompact,
  markAnalyticsReturn,
  readStoredAnalyticsPeriod,
  shouldNavigateAnalyticsRow,
  writeStoredAnalyticsPeriod,
} from '../utils/analyticsPolicy'
import { formatEuroFr } from '../utils/editProductOutcome'
import '../styles/scrollbar.css'
import './Analytics.css'

function useThemeName() {
  const [theme, setTheme] = useState(() => (
    typeof document === 'undefined' ? 'dark' : document.documentElement.getAttribute('data-theme') || 'dark'
  ))
  useEffect(() => {
    const root = document.documentElement
    const update = () => setTheme(root.getAttribute('data-theme') || 'dark')
    const observer = new MutationObserver(update)
    observer.observe(root, { attributes: true, attributeFilter: ['data-theme'] })
    return () => observer.disconnect()
  }, [])
  return theme
}

function AxisTick({ x, y, payload, data }) {
  const fullName = data?.find((item) => item.name === payload.value)?.fullName || payload.value
  return (
    <text
      x={x}
      y={y}
      dy={4}
      textAnchor="end"
      fill="currentColor"
      fontSize={12}
    >
      <title>{fullName}</title>
      {payload.value}
    </text>
  )
}

function ChartTooltip({ active, payload, label, money = false, extra }) {
  if (!active || !payload?.length) return null
  const full = payload[0]?.payload?.fullName || payload[0]?.payload?.fullDate || label
  return (
    <div className="analytics-tooltip" role="status">
      {full ? <p className="analytics-tooltip-label">{full}</p> : null}
      {payload.map((entry) => (
        <p key={entry.dataKey} className="analytics-tooltip-row">
          <span>{entry.name}</span>
          <strong>{money ? formatEuroFr(entry.value) : extra?.(entry) || entry.value}</strong>
        </p>
      ))}
    </div>
  )
}

function Section({ title, hint, children, wide = false }) {
  return (
    <section className={`analytics-section${wide ? ' is-wide' : ''}`}>
      <header>
        <h3>{title}</h3>
        {hint ? <p>{hint}</p> : null}
      </header>
      {children}
    </section>
  )
}

function ChartFrame({ id, title, onOpen, children }) {
  return (
    <div className="analytics-chart">
      <div className="analytics-chart-head">
        <h4>{title}</h4>
        <button type="button" className="analytics-expand" onClick={() => onOpen(id)} aria-label={`Agrandir ${title}`}>
          <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
            <path d="M9 4H4v5M15 4h5v5M9 20H4v-5M15 20h5v-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </button>
      </div>
      <div className="analytics-chart-body">
        {children}
      </div>
    </div>
  )
}

export function CartsTable({ rows, persist = true }) {
  const navigate = useNavigate()
  const originRef = useRef({ x: 0, y: 0, dragged: false })

  const go = (id) => {
    if (!id || !persist) return
    markAnalyticsReturn()
    navigate(`/carts/${id}`, { state: { fromAnalytics: true } })
  }

  const markPointer = (event) => {
    originRef.current = { x: event.clientX, y: event.clientY, dragged: false }
  }

  const trackPointer = (event) => {
    if (event.buttons === 0 && event.pointerType !== 'touch') return
    const moved = Math.hypot(
      event.clientX - originRef.current.x,
      event.clientY - originRef.current.y,
    ) > 8
    if (moved) originRef.current.dragged = true
  }

  const wasDragged = () => originRef.current.dragged

  return (
    <div
      className="analytics-table-wrap"
      role="region"
      aria-label="Détails des paniers, défilement horizontal"
      onPointerDown={markPointer}
      onPointerMove={trackPointer}
    >
      <p className="analytics-table-hint">Glisser horizontalement pour voir toutes les colonnes</p>
      <table className="analytics-table">
        <thead>
          <tr>
            <th>Nom</th>
            <th>Valeur</th>
            <th>Budget</th>
            <th>Écart</th>
            <th>Créé le</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((cart) => {
            const canOpen = Boolean(cart.id)
            return (
              <tr
                key={cart.id || cart.fullName}
                className={canOpen ? 'is-nav' : 'is-unavailable'}
                tabIndex={canOpen ? 0 : undefined}
                aria-disabled={canOpen ? undefined : true}
                title={canOpen ? cart.fullName : 'Panier indisponible'}
                onPointerDown={markPointer}
                onClick={(event) => {
                  const moved = wasDragged() || Math.hypot(
                    event.clientX - originRef.current.x,
                    event.clientY - originRef.current.y,
                  ) > 8
                  if (!shouldNavigateAnalyticsRow({
                    hasId: canOpen,
                    isInteractiveTarget: Boolean(event.target.closest('a, button, input')),
                    hasTextSelection: Boolean(window.getSelection?.()?.toString()),
                    pointerMoved: moved,
                  })) return
                  go(cart.id)
                }}
                onKeyDown={(event) => {
                  if (!canOpen) return
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    go(cart.id)
                  }
                }}
              >
                <td>
                  {canOpen && persist ? (
                    <Link
                      className="analytics-cart-link"
                      to={`/carts/${cart.id}`}
                      state={{ fromAnalytics: true }}
                      onClick={(event) => {
                        const moved = wasDragged() || Math.hypot(
                          event.clientX - originRef.current.x,
                          event.clientY - originRef.current.y,
                        ) > 8
                        if (moved) {
                          event.preventDefault()
                          event.stopPropagation()
                          return
                        }
                        markAnalyticsReturn()
                      }}
                    >
                      {cart.fullName}
                    </Link>
                  ) : (
                    <span className={canOpen ? 'analytics-cart-link' : 'analytics-cart-missing'}>{cart.fullName || 'Panier indisponible'}</span>
                  )}
                </td>
                <td className="is-num">{formatEuroFr(cart.total)}</td>
                <td className="is-num">{cart.budget > 0 ? formatEuroFr(cart.budget) : '—'}</td>
                <td className={`is-num${cart.gap == null ? '' : cart.gap >= 0 ? ' is-pos' : ' is-neg'}`}>
                  {cart.gap == null ? '—' : formatEuroFr(cart.gap)}
                </td>
                <td>{cart.date}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function Analytics() {
  const reduced = usePrefersReducedMotion()
  // Sur écran étroit, les axes de catégorie et les rayons doivent laisser
  // la place aux barres : sinon il ne reste qu'une quarantaine de pixels utiles.
  const narrow = useIsNarrow(720)
  const catAxisWidth = narrow ? 96 : 150
  const catAxisWideWidth = narrow ? 96 : 170
  const numAxisWidth = narrow ? 52 : 88
  // Sur étroit, les montants sont abrégés (k€, M€) pour tenir dans l'axe.
  const formatAxisAmount = narrow ? formatEuroAxisCompact : formatEuroFr
  const pieRadius = narrow ? 74 : 92
  const modalPieRadius = narrow ? 92 : 140
  const modalAxisWidth = narrow ? 104 : 180
  const compactModal = useMediaQuery('(max-width: 768px)')
  const theme = useThemeName()
  const colors = analyticsSeriesColors(theme)
  const [period, setPeriod] = useState(readStoredAnalyticsPeriod)
  const [selectedChart, setSelectedChart] = useState(null)
  const [hostHeight, setHostHeight] = useState(0)
  const chartHostRef = useRef(null)
  const axis = theme === 'light' ? 'rgba(18, 20, 26, 0.55)' : 'rgba(247, 247, 248, 0.55)'
  const grid = theme === 'light' ? 'rgba(18, 20, 26, 0.08)' : 'rgba(255, 255, 255, 0.08)'
  const animate = !reduced
  const chartKey = period

  const { data: cartsData, isLoading } = useCartsQuery()
  const carts = cartsData ?? []
  const { data: allItems = [], isLoading: isLoadingItems } = useQuery({
    queryKey: ['all-items-for-analytics', carts.map((cart) => cart.id).join(',')],
    queryFn: async () => {
      if (!carts.length) return []
      const items = await itemsService.listAll()
      return items.map((item) => {
        const cart = carts.find((entry) => entry.id === item.cart_id)
        return { ...item, cart_name: item.cart_name || cart?.name }
      })
    },
    enabled: carts.length > 0,
    retry: false,
  })

  const model = useMemo(
    () => buildAnalyticsModel(carts, allItems, { period }),
    [carts, allItems, period],
  )
  const details = chartDetailsFor(model, selectedChart)
  const closeRef = useRef(null)
  const restoredRef = useRef(false)
  const categoryCount = details?.data?.length || 0
  const isCategoryChart = Boolean(details?.data?.[0]?.fullName)
    && selectedChart !== 'repartition'
    && selectedChart !== 'evolution'
    && selectedChart !== 'details-paniers'
  const filledHeight = hostHeight > 0 ? hostHeight : 0
  const modalChartHeight = compactModal
    ? (isCategoryChart && filledHeight ? Math.max(filledHeight, categoryCount * 34) : (filledHeight || '100%'))
    : (narrow ? 320 : 420)
  const modalPieSize = compactModal
    ? Math.max(72, Math.min(Math.floor((filledHeight || 320) * 0.36), 160))
    : modalPieRadius

  useLayoutEffect(() => {
    if (!selectedChart) {
      setHostHeight(0)
      return undefined
    }
    const el = chartHostRef.current
    if (!el) return undefined
    const update = () => setHostHeight(el.clientHeight)
    update()
    if (typeof ResizeObserver !== 'function') {
      window.addEventListener('resize', update)
      return () => window.removeEventListener('resize', update)
    }
    const observer = new ResizeObserver(update)
    observer.observe(el)
    return () => observer.disconnect()
  }, [selectedChart])

  useEffect(() => {
    writeStoredAnalyticsPeriod(period)
  }, [period])

  useLayoutEffect(() => {
    if (isLoading || isLoadingItems || restoredRef.current) return
    const y = consumeAnalyticsScroll()
    restoredRef.current = true
    if (y != null) window.scrollTo(0, y)
  }, [isLoading, isLoadingItems])

  const handlePeriod = (next) => {
    if (next === period) return
    setPeriod(next)
  }

  if (isLoading || isLoadingItems) {
    return (
      <div className="analytics-page">
        <div className="analytics-state"><div className="loader" /><p>Chargement des statistiques…</p></div>
      </div>
    )
  }

  if (!carts.length) {
    return (
      <div className="analytics-page">
        <div className="analytics-state">
          <h2>Statistiques</h2>
          <p>Aucune donnée disponible. Créez des paniers pour voir la valeur de vos listes.</p>
        </div>
      </div>
    )
  }

  const barMargin = narrow
    ? { top: 8, right: 6, left: 0, bottom: 8 }
    : { top: 8, right: 16, left: 8, bottom: 8 }
  const vBarMargin = narrow
    ? { top: 8, right: 8, left: 0, bottom: 8 }
    : { top: 8, right: 24, left: 8, bottom: 8 }
  const cumulTooltip = <ChartTooltip money />

  return (
    <div className="analytics-page">
      <header className="analytics-hero">
        <h2>Statistiques</h2>
        <p>Valeurs actuelles de vos paniers. Les prix ne sont pas un historique d’achats.</p>
        <AnalyticsPeriodSlider value={period} onCommit={handlePeriod} reducedMotion={reduced} />
      </header>

      <div className="analytics-kpis">
        <div className="analytics-kpi">
          <span className="analytics-kpi-icon" aria-hidden="true">▣</span>
          <strong>{model.totalCarts}</strong>
          <span>Paniers</span>
        </div>
        <div className="analytics-kpi">
          <span className="analytics-kpi-icon" aria-hidden="true">€</span>
          <strong>{formatEuroFr(model.cartValueTotal)}</strong>
          <span>{model.labels.cartValue}</span>
        </div>
        <div className="analytics-kpi">
          <span className="analytics-kpi-icon" aria-hidden="true">~</span>
          <strong>{formatEuroFr(model.averageCartValue)}</strong>
          <span>{model.labels.average}</span>
        </div>
        <div className="analytics-kpi">
          <span className="analytics-kpi-icon" aria-hidden="true">◎</span>
          <strong>{model.budgetUtilization == null ? '—' : `${model.budgetUtilization.toFixed(1)} %`}</strong>
          <span>{model.labels.utilization}</span>
        </div>
        <div className="analytics-kpi">
          <span className="analytics-kpi-icon" aria-hidden="true">#</span>
          <strong>{model.references}</strong>
          <span>Références · {model.units} unités</span>
        </div>
      </div>
      <p className="analytics-note">{model.labels.utilizationHint}</p>

      <Section title="Vue générale" hint={model.labels.timeSeriesHint}>
        <div className="analytics-grid">
          <ChartFrame id="evolution" title={model.labels.timeSeries} onOpen={setSelectedChart}>
            <CumulativeValueChart
              data={model.timeSeries}
              height={280}
              colors={colors}
              axis={axis}
              grid={grid}
              animate={animate}
              replayToken={period}
              tooltip={cumulTooltip}
            />
          </ChartFrame>
          {model.monthlyData.length > 0 ? (
            <ChartFrame id="evolution-mensuelle" title={model.labels.monthly} onOpen={setSelectedChart}>
              <ResponsiveContainer width="100%" height={280} key={`month-${chartKey}`}>
                <BarChart data={model.monthlyData} margin={barMargin}>
                  <CartesianGrid strokeDasharray="3 3" stroke={grid} />
                  <XAxis dataKey="month" stroke={axis} />
                  <YAxis stroke={axis} tickFormatter={(value) => formatAxisAmount(value)} width={numAxisWidth} label={narrow ? undefined : { value: 'Valeur', angle: -90, position: 'insideLeft' }} />
                  <Tooltip content={<ChartTooltip money />} />
                  <Bar dataKey="total" name="Valeur des paniers créés" fill={colors[1]} isAnimationActive={animate} radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartFrame>
          ) : null}
        </div>
      </Section>

      <Section title="Budgets" hint={model.labels.utilizationHint}>
        {model.budgetComparison.length ? (
          <ChartFrame id="budget-comparison" title={model.labels.budget} onOpen={setSelectedChart}>
            <ResponsiveContainer width="100%" height={Math.max(280, model.budgetComparison.length * 42)} key={`budg-${chartKey}`}>
              <BarChart data={model.budgetComparison} layout="vertical" margin={vBarMargin}>
                <CartesianGrid strokeDasharray="3 3" stroke={grid} />
                <XAxis type="number" stroke={axis} tickFormatter={(value) => formatAxisAmount(value)} label={narrow ? undefined : { value: 'Montant', position: 'insideBottom', offset: -2 }} />
                <YAxis type="category" dataKey="name" stroke={axis} width={catAxisWidth} tick={(props) => <AxisTick {...props} data={model.budgetComparison} />} />
                <Tooltip content={<ChartTooltip money />} />
                <Legend />
                <Bar dataKey="budget" name="Budget" fill={colors[1]} isAnimationActive={animate} />
                <Bar dataKey="valeur" name="Valeur actuelle" fill={colors[0]} isAnimationActive={animate} />
              </BarChart>
            </ResponsiveContainer>
          </ChartFrame>
        ) : (
          <p className="analytics-empty">Aucun panier n’a de budget. Ils restent comptés dans la valeur globale, pas dans le taux d’utilisation.</p>
        )}
      </Section>

      <Section title="Répartition">
        <div className="analytics-grid">
          <ChartFrame id="valeur-par-panier" title="Valeur par panier" onOpen={setSelectedChart}>
            <ResponsiveContainer width="100%" height={Math.max(280, Math.min(8, model.cartRows.length) * 36)} key={`val-${chartKey}`}>
              <BarChart data={model.cartRows.slice(0, 8)} layout="vertical" margin={vBarMargin}>
                <CartesianGrid strokeDasharray="3 3" stroke={grid} />
                <XAxis type="number" stroke={axis} tickFormatter={(value) => formatAxisAmount(value)} label={narrow ? undefined : { value: 'Valeur', position: 'insideBottom', offset: -2 }} />
                <YAxis type="category" dataKey="name" stroke={axis} width={catAxisWidth} tick={(props) => <AxisTick {...props} data={model.cartRows} />} />
                <Tooltip content={<ChartTooltip money />} />
                <Bar dataKey="total" name="Valeur actuelle" fill={colors[0]} isAnimationActive={animate} />
              </BarChart>
            </ResponsiveContainer>
          </ChartFrame>
          {model.pieData.length ? (
            <ChartFrame id="repartition" title={model.labels.distribution} onOpen={setSelectedChart}>
              <ResponsiveContainer width="100%" height={280} key={`pie-${chartKey}`}>
                <PieChart>
                  <Pie data={model.pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={pieRadius} isAnimationActive={animate}>
                    {model.pieData.map((entry, index) => (
                      <Cell key={entry.fullName || entry.name} fill={colors[index % colors.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<ChartTooltip money />} />
                </PieChart>
              </ResponsiveContainer>
              <ul className="analytics-legend">
                {model.pieData.map((entry, index) => {
                  const total = model.pieData.reduce((sum, item) => sum + item.value, 0) || 1
                  return (
                    <li key={entry.fullName || entry.name}>
                      <span style={{ background: colors[index % colors.length] }} />
                      <em title={entry.fullName}>{entry.fullName}</em>
                      <strong>{Math.round((entry.value / total) * 100)} %</strong>
                    </li>
                  )
                })}
              </ul>
            </ChartFrame>
          ) : null}
        </div>
      </Section>

      <Section title="Détail des produits" hint="Les références sont des lignes. Les unités additionnent les quantités.">
        <div className="analytics-grid">
          {model.mostFrequentProducts.length ? (
            <ChartFrame id="produits-frequents" title="Produits les plus fréquents" onOpen={setSelectedChart}>
              <ResponsiveContainer width="100%" height={Math.max(280, model.mostFrequentProducts.length * 32)} key={`freq-${chartKey}`}>
                <BarChart data={model.mostFrequentProducts} layout="vertical" margin={vBarMargin}>
                  <CartesianGrid strokeDasharray="3 3" stroke={grid} />
                  <XAxis type="number" stroke={axis} label={narrow ? undefined : { value: 'Références', position: 'insideBottom', offset: -2 }} />
                  <YAxis type="category" dataKey="name" stroke={axis} width={catAxisWideWidth} tick={(props) => <AxisTick {...props} data={model.mostFrequentProducts} />} />
                  <Tooltip content={<ChartTooltip extra={(entry) => `${entry.value} référence(s)`} />} />
                  <Bar dataKey="count" name="Références" fill={colors[4]} isAnimationActive={animate} />
                </BarChart>
              </ResponsiveContainer>
            </ChartFrame>
          ) : null}
          {model.topExpensiveProducts.length ? (
            <ChartFrame id="produits-chers" title="Prix unitaires les plus élevés" onOpen={setSelectedChart}>
              <ResponsiveContainer width="100%" height={Math.max(280, model.topExpensiveProducts.length * 32)} key={`exp-${chartKey}`}>
                <BarChart data={model.topExpensiveProducts} layout="vertical" margin={vBarMargin}>
                  <CartesianGrid strokeDasharray="3 3" stroke={grid} />
                  <XAxis type="number" stroke={axis} tickFormatter={(value) => formatAxisAmount(value)} label={narrow ? undefined : { value: 'Prix unitaire', position: 'insideBottom', offset: -2 }} />
                  <YAxis type="category" dataKey="name" stroke={axis} width={catAxisWideWidth} tick={(props) => <AxisTick {...props} data={model.topExpensiveProducts} />} />
                  <Tooltip content={<ChartTooltip money />} />
                  <Bar dataKey="price" name="Prix unitaire actuel" fill={colors[2]} isAnimationActive={animate} />
                </BarChart>
              </ResponsiveContainer>
            </ChartFrame>
          ) : null}
          {model.priceDistribution.length ? (
            <ChartFrame id="distribution-prix" title="Distribution des prix unitaires" onOpen={setSelectedChart}>
              <ResponsiveContainer width="100%" height={260} key={`dist-${chartKey}`}>
                <BarChart data={model.priceDistribution} margin={barMargin}>
                  <CartesianGrid strokeDasharray="3 3" stroke={grid} />
                  <XAxis dataKey="label" stroke={axis} />
                  <YAxis stroke={axis} allowDecimals={false} width={narrow ? 36 : 60} label={narrow ? undefined : { value: 'Références', angle: -90, position: 'insideLeft' }} />
                  <Tooltip content={<ChartTooltip extra={(entry) => `${entry.value} référence(s)`} />} />
                  <Bar dataKey="count" name="Références" fill={colors[1]} isAnimationActive={animate} />
                </BarChart>
              </ResponsiveContainer>
            </ChartFrame>
          ) : null}
        </div>
      </Section>

      <Section title="Détails des paniers" wide>
        <CartsTable rows={model.cartRows} />
      </Section>

      <GlassOverlay
        isOpen={!!selectedChart}
        onClose={() => setSelectedChart(null)}
        labelledBy="analytics-modal-title"
        returnFocusRef={closeRef}
        panelClassName="analytics-modal-panel"
        rootClassName="analytics-modal-root"
        followPointer={false}
      >
        {details ? (
          <div className="analytics-modal">
            <div className="analytics-modal-scroll lg-scroll">
              <header className="analytics-modal-header">
                <h2 id="analytics-modal-title">{details.title}</h2>
              </header>
              {details.description ? <p className="analytics-modal-copy">{details.description}</p> : null}
              <p className="scp-summary analytics-modal-meta">
                {details.total != null ? <>Total {formatEuroFr(details.total)}</> : null}
                {details.totalBudget != null ? <> · Budget {formatEuroFr(details.totalBudget)}</> : null}
                {details.totalValeur != null ? <> · Valeur {formatEuroFr(details.totalValeur)}</> : null}
              </p>
              <div
                ref={chartHostRef}
                className={`analytics-modal-chart${compactModal ? ' is-fill' : ''}${isCategoryChart ? ' is-categories' : ''}`}
              >
                {selectedChart === 'details-paniers' ? (
                  <CartsTable rows={details.data} />
                ) : selectedChart === 'repartition' ? (
                  <ResponsiveContainer width="100%" height={modalChartHeight}>
                    <PieChart>
                      <Pie data={details.data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={modalPieSize} isAnimationActive={animate}>
                        {details.data.map((entry, index) => (
                          <Cell key={entry.fullName || entry.name} fill={colors[index % colors.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<ChartTooltip money />} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : selectedChart === 'evolution' ? (
                  <CumulativeValueChart
                    data={details.data}
                    height={compactModal ? null : modalChartHeight}
                    colors={colors}
                    axis={axis}
                    grid={grid}
                    animate={animate}
                    replayToken={`${period}-modal`}
                    tooltip={cumulTooltip}
                  />
                ) : (
                  <ResponsiveContainer width="100%" height={modalChartHeight}>
                    <BarChart
                      data={details.data}
                      layout={details.data?.[0]?.fullName ? 'vertical' : 'horizontal'}
                      margin={narrow ? { top: 8, right: 8, left: 0, bottom: 16 } : { top: 8, right: 24, left: 8, bottom: 24 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={grid} />
                      {details.data?.[0]?.fullName ? (
                        <>
                          <XAxis type="number" stroke={axis} tickFormatter={(value) => (typeof details.data[0].total === 'number' || typeof details.data[0].price === 'number' || typeof details.data[0].budget === 'number' ? formatAxisAmount(value) : value)} />
                          <YAxis type="category" dataKey="name" stroke={axis} width={modalAxisWidth} tick={(props) => <AxisTick {...props} data={details.data} />} />
                        </>
                      ) : (
                        <>
                          <XAxis dataKey={details.data?.[0]?.month ? 'month' : 'label'} stroke={axis} />
                          <YAxis stroke={axis} width={narrow ? 46 : 60} />
                        </>
                      )}
                      <Tooltip content={<ChartTooltip money={selectedChart !== 'produits-frequents' && selectedChart !== 'distribution-prix'} />} />
                      <Legend />
                      {selectedChart === 'budget-comparison' ? (
                        <>
                          <Bar dataKey="budget" name="Budget" fill={colors[1]} isAnimationActive={animate} />
                          <Bar dataKey="valeur" name="Valeur actuelle" fill={colors[0]} isAnimationActive={animate} />
                        </>
                      ) : (
                        <Bar
                          dataKey={details.data?.[0]?.price != null ? 'price' : details.data?.[0]?.count != null ? 'count' : 'total'}
                          name={selectedChart === 'produits-frequents' ? 'Références' : selectedChart === 'produits-chers' ? 'Prix unitaire' : 'Valeur actuelle'}
                          fill={colors[0]}
                          isAnimationActive={animate}
                        />
                      )}
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
            <button
              ref={closeRef}
              type="button"
              className="analytics-modal-close"
              aria-label="Fermer le graphique"
              onPointerUp={(event) => {
                if (event.pointerType === 'mouse' && event.button !== 0) return
                event.preventDefault()
                event.stopPropagation()
                setSelectedChart(null)
              }}
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
                setSelectedChart(null)
              }}
            >
              <CloseGlyph />
            </button>
          </div>
        ) : null}
      </GlassOverlay>
    </div>
  )
}

export default Analytics
